// packages/core/src/diff/modules.ts
import { moduleDiffId, type ResolvedModule } from "../resolver.js";
import type { ModuleDiff, Status, ChangeSet } from "../diff-types.js";
import type { Name, StableId } from "../types.js";
import { diffFields, diffAspects } from "./fields-aspects.js";
import { diffInterfaces } from "./interfaces.js";
import { diffSurfaces } from "./surfaces.js";

export function diffModulesTopLevel(
  before: readonly ResolvedModule[],
  after: readonly ResolvedModule[],
  parentPath: readonly Name[] = []
): ChangeSet["modules"] {
  // Match by id — explicit `#id`, else a qualified-name fallback so content
  // with no `#id`s still diffs. `before` and `after` are keyed with the same
  // parentPath, so their fallback ids are comparable.
  const beforeById = new Map<StableId, ResolvedModule>();
  const afterById = new Map<StableId, ResolvedModule>();
  for (const m of before) beforeById.set(moduleDiffId(m, parentPath), m);
  for (const m of after) afterById.set(moduleDiffId(m, parentPath), m);

  const out = new Map<StableId, ModuleDiff>();
  const allIds = new Set<StableId>([...beforeById.keys(), ...afterById.keys()]);
  for (const id of allIds) {
    const b = beforeById.get(id);
    const a = afterById.get(id);
    if (b && a) out.set(id, diffMatched(id, b, a, parentPath));
    else if (a) out.set(id, asAddedRecursive(id, a, parentPath));
    else if (b) out.set(id, asRemovedRecursive(id, b, parentPath));
  }
  return out;
}

function diffMatched(
  id: StableId,
  b: ResolvedModule,
  a: ResolvedModule,
  parentPath: readonly Name[]
): ModuleDiff {
  const renamed = b.name !== a.name;
  const typeChanged = b.typeName !== a.typeName;
  const fields = diffFields(b.body.fields, a.body.fields);
  const aspects = diffAspects(b.body.aspects, a.body.aspects);
  const interfaces = diffInterfaces(b.interfaces, a.interfaces);
  const surfaces = diffSurfaces(b.surfaces, a.surfaces);
  const children = diffModulesTopLevel(b.modules, a.modules, [...parentPath, a.name]);
  const anyInner =
    typeChanged ||
    fields.size > 0 ||
    aspects.size > 0 ||
    [...interfaces.values()].some((i) => i.status !== "unchanged") ||
    [...surfaces.values()].some((f) => f.status !== "unchanged") ||
    [...children.values()].some((c) => c.status !== "unchanged");
  const status: Status = renamed ? "renamed" : anyInner ? "modified" : "unchanged";
  return {
    id,
    name: a.name,
    status,
    nameChange: renamed ? { from: b.name, to: a.name } : undefined,
    typeChange: typeChanged ? { from: b.typeName, to: a.typeName } : undefined,
    fields,
    aspects,
    interfaces,
    surfaces,
    children,
    sourceFile: a.decl.source?.file,
  };
}

function asAddedRecursive(id: StableId, m: ResolvedModule, parentPath: readonly Name[]): ModuleDiff {
  const childPath = [...parentPath, m.name];
  const children = new Map<StableId, ModuleDiff>();
  for (const c of m.modules) {
    const cid = moduleDiffId(c, childPath);
    children.set(cid, asAddedRecursive(cid, c, childPath));
  }
  return {
    id,
    name: m.name,
    status: "added",
    fields: new Map(),
    aspects: new Map(),
    interfaces: new Map(),
    surfaces: new Map(),
    children,
    sourceFile: m.decl.source?.file,
  };
}

function asRemovedRecursive(id: StableId, m: ResolvedModule, parentPath: readonly Name[]): ModuleDiff {
  const childPath = [...parentPath, m.name];
  const children = new Map<StableId, ModuleDiff>();
  for (const c of m.modules) {
    const cid = moduleDiffId(c, childPath);
    children.set(cid, asRemovedRecursive(cid, c, childPath));
  }
  return {
    id,
    name: m.name,
    status: "removed",
    fields: new Map(),
    aspects: new Map(),
    interfaces: new Map(),
    surfaces: new Map(),
    children,
    sourceFile: m.decl.source?.file,
  };
}
